import { useState, useContext, useEffect, useCallback, Fragment } from "react";
import '../../styles/page-titles.css';
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioEntregadores } from "../../services/api";
import { nowTz, formatDateTz } from '../../utils/timezone';
import Loading from "react-loading";
import { TbTruckDelivery } from 'react-icons/tb';
import { FaMotorcycle, FaMoneyBillWave, FaUsers } from 'react-icons/fa';
import { MdDeliveryDining } from 'react-icons/md';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 16px;
`;

const Subtitle = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0 0 24px;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .presets {
        display: flex;
        gap: 6px;
        background: #f1f5f9;
        padding: 4px;
        border-radius: 10px;
        flex-wrap: wrap;
    }

    .presets button {
        padding: 8px 14px;
        background: transparent;
        color: #475569;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        transition: all 0.15s;
    }

    .presets button.ativo {
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: #fff;
        box-shadow: 0 2px 6px rgba(49, 140, 213, 0.35);
    }

    .dates {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button.update {
        padding: 9px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover { opacity: 0.9; }
        &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    @media (max-width: 600px) {
        width: 100%;
        .dates { width: 100%; }
        input[type="date"], button.update { flex: 1; }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ highlight }) => highlight && `
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.08), rgba(76, 217, 100, 0.12));
        border: 1px solid rgba(40, 167, 69, 0.2);
    `}

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label { font-size: 14px; color: #666; }
    .value { font-size: 28px; font-weight: 700; color: #333; }
    .sub { font-size: 12px; color: #999; }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const TableCard = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 24px;
    overflow: hidden;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin: 0 0 20px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    h3 .hint {
        font-size: 12px;
        font-weight: 500;
        color: #94a3b8;
    }

    .table-wrap {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    @media (max-width: 600px) { padding: 16px; }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 640px;

    thead th {
        text-align: left;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #94a3b8;
        font-weight: 600;
        padding: 10px 12px;
        border-bottom: 2px solid #eef2f6;
        white-space: nowrap;
    }

    thead th.num, tbody td.num { text-align: right; }

    tbody td {
        padding: 14px 12px;
        border-bottom: 1px solid #f1f5f9;
        font-size: 14px;
        color: #1e293b;
        white-space: nowrap;
    }

    tbody tr.grupo { cursor: pointer; }
    tbody tr.grupo:hover { background: #f8fafc; }

    tbody tr.unassigned td {
        color: #94a3b8;
        font-style: italic;
        background: #fafbfc;
    }

    .driver {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
    }

    .driver .chevron {
        color: #94a3b8;
        display: flex;
        align-items: center;
        font-size: 16px;
        flex-shrink: 0;
    }

    /* Linha de detalhe: as entregas daquele entregador */
    tbody tr.detalhe > td {
        padding: 0;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    .detalhe-wrap {
        padding: 12px 12px 16px 46px;
    }

    .detalhe-titulo {
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #64748b;
        margin-bottom: 8px;
        font-style: normal;
    }

    table.detalhe-tabela {
        width: 100%;
        border-collapse: collapse;
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
    }

    table.detalhe-tabela th {
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #94a3b8;
        font-weight: 600;
        padding: 8px 10px;
        border-bottom: 1px solid #eef2f6;
        background: #fcfdfe;
        white-space: nowrap;
    }

    table.detalhe-tabela td {
        padding: 9px 10px;
        border-bottom: 1px solid #f4f7fa;
        font-size: 13px;
        color: #334155;
        font-style: normal;
        white-space: nowrap;
    }

    table.detalhe-tabela tr:last-child td { border-bottom: none; }
    table.detalhe-tabela th.num, table.detalhe-tabela td.num { text-align: right; }
    table.detalhe-tabela td.pedido { font-weight: 700; color: #1e293b; }
    table.detalhe-tabela td.endereco {
        white-space: normal;
        min-width: 180px;
        color: #64748b;
    }

    .driver .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(49, 140, 213, 0.1);
        color: rgb(49, 140, 213);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        flex-shrink: 0;
    }

    td.fees { font-weight: 700; color: #28a745; }

    tfoot td {
        padding: 14px 12px;
        border-top: 2px solid #eef2f6;
        font-size: 14px;
        font-weight: 700;
        color: #333;
    }
    tfoot td.num { text-align: right; }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: #94a3b8;
    gap: 12px;

    svg { font-size: 44px; opacity: 0.5; }
    span { font-size: 15px; }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const PRESETS = [
    { key: 'hoje', days: 0 },
    { key: '7dias', days: 6 },
    { key: '15dias', days: 14 },
    { key: '30dias', days: 29 },
];

const RelatorioEntregadores = () => {
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: { total_entregas: 0, total_taxas: 0, total_faturado: 0, entregadores_ativos: 0, taxa_media: 0 },
        entregadores: []
    });
    const [preset, setPreset] = useState('7dias');
    const [startDate, setStartDate] = useState(nowTz().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));
    const [expandidos, setExpandidos] = useState([]);

    const chaveGrupo = (e, idx) => e.id || `unassigned-${idx}`;

    const alternarGrupo = (chave) => {
        setExpandidos(prev => prev.includes(chave)
            ? prev.filter(c => c !== chave)
            : [...prev, chave]);
    };

    const applyPreset = (key) => {
        const found = PRESETS.find(p => p.key === key);
        if (!found) return;
        setPreset(key);
        setStartDate(nowTz().subtract(found.days, 'days').format('YYYY-MM-DD'));
        setEndDate(nowTz().format('YYYY-MM-DD'));
    };

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioEntregadores(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
                // Periodo novo, linhas novas: as chaves antigas nao valem mais.
                setExpandidos([]);
            }
        } catch (error) {
            console.error('[RELATORIO-ENTREGADORES] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const resumo = data.resumo || {};
    const entregadores = data.entregadores || [];

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><TbTruckDelivery /></div>
                            <h1 className="page-title">{t('drivers.title')}</h1>
                        </div>
                        <Filters>
                            <div className="presets">
                                {PRESETS.map(p => (
                                    <button
                                        key={p.key}
                                        className={preset === p.key ? 'ativo' : ''}
                                        onClick={() => applyPreset(p.key)}
                                    >
                                        {t(`performance.${p.key === 'hoje' ? 'today' : p.key === '7dias' ? 'last7' : p.key === '15dias' ? 'last15' : 'last30'}`)}
                                    </button>
                                ))}
                            </div>
                            <div className="dates">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => { setStartDate(e.target.value); setPreset('custom'); }}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setPreset('custom'); }}
                                />
                                <button className="update" onClick={fetchData} disabled={isLoading}>
                                    {isLoading ? t('common.loading') : t('common.update')}
                                </button>
                            </div>
                        </Filters>
                    </Header>
                    <Subtitle>{t('drivers.subtitle')}</Subtitle>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard highlight>
                                    <div className="icon green"><FaMoneyBillWave /></div>
                                    <span className="label">{t('drivers.totalFees')}</span>
                                    <span className="value">{formatPriceWithSymbol(resumo.total_taxas || 0)}</span>
                                    <span className="sub">{t('drivers.feesToSettle')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon blue"><MdDeliveryDining /></div>
                                    <span className="label">{t('drivers.totalDeliveries')}</span>
                                    <span className="value">{resumo.total_entregas || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple"><FaUsers /></div>
                                    <span className="label">{t('drivers.activeDrivers')}</span>
                                    <span className="value">{resumo.entregadores_ativos || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange"><FaMotorcycle /></div>
                                    <span className="label">{t('drivers.avgFeePerDelivery')}</span>
                                    <span className="value">{formatPriceWithSymbol(resumo.taxa_media || 0)}</span>
                                    <span className="sub">{t('common.averageFee')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <TableCard>
                                <h3>
                                    <TbTruckDelivery /> {t('drivers.tableTitle')}
                                    {entregadores.some(e => (e.pedidos || []).length > 0) && (
                                        <span className="hint">{t('drivers.expandHint')}</span>
                                    )}
                                </h3>
                                {entregadores.length === 0 ? (
                                    <EmptyState>
                                        <MdDeliveryDining />
                                        <span>{t('drivers.noDeliveries')}</span>
                                    </EmptyState>
                                ) : (
                                    <div className="table-wrap">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>{t('drivers.colDriver')}</th>
                                                    <th>{t('drivers.colVehicle')}</th>
                                                    <th className="num">{t('drivers.colDeliveries')}</th>
                                                    <th className="num">{t('drivers.colFees')}</th>
                                                    <th className="num">{t('drivers.colAvgFee')}</th>
                                                    <th className="num">{t('drivers.colRevenue')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {entregadores.map((e, idx) => {
                                                    const chave = chaveGrupo(e, idx);
                                                    const pedidos = e.pedidos || [];
                                                    // O backend manda `pedidos` numa versao mais nova que o front.
                                                    // Enquanto o Fly nao subir, nao ofereça expansao vazia.
                                                    const expansivel = pedidos.length > 0;
                                                    const aberto = expansivel && expandidos.includes(chave);

                                                    return (
                                                        <Fragment key={chave}>
                                                            <tr
                                                                className={`${expansivel ? 'grupo' : ''}${e.sem_entregador ? ' unassigned' : ''}`}
                                                                onClick={expansivel ? () => alternarGrupo(chave) : undefined}
                                                            >
                                                                <td>
                                                                    <div className="driver">
                                                                        {expansivel && (
                                                                            <span className="chevron">
                                                                                {aberto ? <FiChevronDown /> : <FiChevronRight />}
                                                                            </span>
                                                                        )}
                                                                        {!e.sem_entregador && (
                                                                            <span className="avatar"><FaMotorcycle /></span>
                                                                        )}
                                                                        {e.sem_entregador ? t('drivers.unassigned') : (e.nome || '—')}
                                                                    </div>
                                                                </td>
                                                                <td>{e.veiculo || '—'}</td>
                                                                <td className="num">{e.entregas || 0}</td>
                                                                <td className="num fees">{formatPriceWithSymbol(e.total_taxas || 0)}</td>
                                                                <td className="num">{formatPriceWithSymbol(e.taxa_media || 0)}</td>
                                                                <td className="num">{formatPriceWithSymbol(e.total_faturado || 0)}</td>
                                                            </tr>

                                                            {aberto && (
                                                                <tr className="detalhe">
                                                                    <td colSpan={6}>
                                                                        <div className="detalhe-wrap">
                                                                            <div className="detalhe-titulo">
                                                                                {e.sem_entregador
                                                                                    ? t('drivers.unassignedDeliveries')
                                                                                    : t('drivers.deliveriesOf', { nome: e.nome || '—' })}
                                                                            </div>
                                                                            <table className="detalhe-tabela">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>{t('drivers.colOrder')}</th>
                                                                                        <th>{t('drivers.colDate')}</th>
                                                                                        <th>{t('drivers.colCustomer')}</th>
                                                                                        <th>{t('drivers.colAddress')}</th>
                                                                                        <th>{t('drivers.colPayment')}</th>
                                                                                        <th className="num">{t('drivers.colFee')}</th>
                                                                                        <th className="num">{t('drivers.colOrderTotal')}</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {pedidos.map((p, i) => (
                                                                                        <tr key={`${chave}-${p.id_pedido_counter || i}`}>
                                                                                            <td className="pedido">
                                                                                                {p.id_pedido_counter ? `#${p.id_pedido_counter}` : '—'}
                                                                                            </td>
                                                                                            <td>{p.data ? formatDateTz(p.data, 'DD/MM HH:mm') : '—'}</td>
                                                                                            <td>{p.cliente || '—'}</td>
                                                                                            <td className="endereco">{p.endereco || '—'}</td>
                                                                                            <td>{p.tipo_pagamento || '—'}</td>
                                                                                            <td className="num">{formatPriceWithSymbol(p.taxa || 0)}</td>
                                                                                            <td className="num">{formatPriceWithSymbol(p.valor_total || 0)}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Fragment>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td>{t('common.total', 'Total')}</td>
                                                    <td></td>
                                                    <td className="num">{resumo.total_entregas || 0}</td>
                                                    <td className="num">{formatPriceWithSymbol(resumo.total_taxas || 0)}</td>
                                                    <td className="num">{formatPriceWithSymbol(resumo.taxa_media || 0)}</td>
                                                    <td className="num">{formatPriceWithSymbol(resumo.total_faturado || 0)}</td>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                )}
                            </TableCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioEntregadores;
